/**
 * E002-T044: Status Tracking Section Component
 * S020-8.20.6: Column-Level Conflict Detection
 *
 * The status tracking of the primary table, kept light: one switch, then what
 * happens to a processed row (set a status / delete the row), then the status
 * column and its values. Every step only appears once the one before it is
 * set, and each change animates its region. Added from the unused settings,
 * tracking starts switched on; switched off, it goes back there.
 *
 * Delete mode always retains failed, suspended and cancelled rows. It is not
 * available while the automation writes to this table: the row a run has
 * just written would be deleted with it. Switching
 * tracking off while a schedule that requires it is active pauses that
 * schedule first.
 */

import { Button, ConfirmModal, notify, Switch } from '@affine/component';
import { AnimatedPillSegment } from '@affine/core/components/animation/animated-pill-segment';
import { AnimatedRegion } from '@affine/core/components/animation/animated-region';
import { useI18n } from '@affine/i18n';
import {
  InformationFillDuotoneIcon,
  LockIcon,
  PlusIcon,
} from '@blocksuite/icons/rc';
import type { FC } from 'react';
import { useCallback, useState } from 'react';

import type {
  AutomationConfig,
  StatusTrackingConfig,
} from '../../services/automation';
import {
  DEFAULT_STATUS_TRACKING_CONFIG,
  STATUS_STANDARD_VALUES,
} from '../../services/automation';
import type { ColumnConflict } from '../../services/automation-discovery';
import type { ColumnInfo } from '../../services/database-discovery';
import { watchesStatusColumn } from '../../types';
import {
  needsStatusTracking,
  writesToSourceTable,
} from '../automation-input-requirements';
import * as settingStyles from '../automation-settings-panel.css';
import { CollapsibleSection } from './collapsible-section';
import { SectionHint, SectionNotice } from './section-layout';
import { useConcealSetting } from './setting-section';
import { SettingSelect } from './setting-select';
import * as styles from './status-tracking-section.css';
import { TypedFieldSelect } from './typed-field-select';

/**
 * "Delete row after success" is hidden from the UI (User 21.10.2026, card
 * dec-aso-delete-row-mode-1021): finished rows are removed with the table
 * cleanup node instead. An automation that already deletes its rows still
 * shows the choice, so the user can switch it to the status column.
 */
const OFFER_DELETE_ROW_MODE = false;

type TrackingMode = NonNullable<StatusTrackingConfig['mode']>;

interface StatusTrackingSectionProps {
  disabled?: boolean;
  /** Status tracking is required (a schedule over a source table). */
  required?: boolean;
  /** Pauses the active schedule on the backend; resolves false on failure. */
  onDeactivateSchedule?: () => Promise<boolean>;
  config: AutomationConfig;
  onUpdate: (updates: Partial<AutomationConfig>) => void;
  /** Available select columns from source table */
  selectColumns?: ColumnInfo[];
  /** S020-8.20.6: Map of column IDs to conflict info */
  usedColumns?: Map<string, ColumnConflict>;
  /** S020-8.20.6: Callback to navigate to conflicting automation */
  onNavigateToConflict?: (automationId: string, docId: string) => void;
  /** Create a status Select column in the primary input table */
  onCreateStatusColumn?: () => Promise<void> | void;
  /** Callback when the section info icon is clicked */
  onInfoClick?: () => void;
}

export const StatusTrackingSection: FC<StatusTrackingSectionProps> = ({
  disabled = false,
  required = false,
  onDeactivateSchedule,
  config,
  onUpdate,
  selectColumns = [],
  usedColumns = new Map(),
  onNavigateToConflict,
  onCreateStatusColumn,
  onInfoClick,
}) => {
  const t = useI18n();
  const concealSetting = useConcealSetting();
  const statusTracking =
    config.statusTracking ?? DEFAULT_STATUS_TRACKING_CONFIG;
  const [conflict, setConflict] = useState<ColumnConflict | null>(null);
  // A column the "Row changed" trigger watches: the automation would start
  // itself by writing it, so it is not taken (assertWatchedColumns).
  const [watchedConflict, setWatchedConflict] = useState<string | null>(null);
  const [creatingColumn, setCreatingColumn] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const mode: TrackingMode = statusTracking.mode ?? 'status_column';
  const deleting = mode === 'delete_row';
  const offersModeChoice = OFFER_DELETE_ROW_MODE || deleting;
  const writesSource = writesToSourceTable(config);

  const updateStatusTracking = useCallback(
    (updates: Partial<StatusTrackingConfig>) => {
      if (disabled) return;
      onUpdate({ statusTracking: { ...statusTracking, ...updates } });
    },
    [disabled, onUpdate, statusTracking]
  );

  // sl-optc: A schedule that requires status tracking would reprocess every
  // row without it, so switching tracking off or clearing its column pauses
  // that schedule — on the backend first, then in the block config.
  // `applied` runs once the change is in the config.
  const updateWithoutTracking = useCallback(
    (updates: Partial<StatusTrackingConfig>, applied?: () => void) => {
      if (disabled) return;
      const triggerConfig = config.triggerConfig;
      if (!triggerConfig?.enabled || !needsStatusTracking(config)) {
        updateStatusTracking(updates);
        applied?.();
        return;
      }
      const pause = () => {
        notify({
          title: t['com.streamlain.streamlain.tracking.schedule-deactivated'](),
          message:
            t['com.streamlain.streamlain.tracking.schedule-deactivated-msg'](),
          style: 'normal',
          theme: 'warning',
        });
        onUpdate({
          statusTracking: { ...statusTracking, ...updates },
          triggerConfig: { ...triggerConfig, enabled: false },
        });
        applied?.();
      };
      if (!onDeactivateSchedule) {
        pause();
        return;
      }
      void onDeactivateSchedule().then(paused => {
        if (paused) pause();
      });
    },
    [
      config,
      disabled,
      onDeactivateSchedule,
      onUpdate,
      statusTracking,
      t,
      updateStatusTracking,
    ]
  );

  // Switched off, tracking goes back among the unused settings.
  const handleToggle = useCallback(
    (checked: boolean) =>
      checked
        ? updateStatusTracking({ enabled: true })
        : updateWithoutTracking({ enabled: false }, () =>
            concealSetting('tracking')
          ),
    [concealSetting, updateStatusTracking, updateWithoutTracking]
  );

  const handleMode = useCallback(
    (next: TrackingMode) => {
      if (next === mode) return;
      if (next !== 'delete_row') updateStatusTracking({ mode: next });
      else if (!writesSource) setConfirmDelete(true);
    },
    [mode, updateStatusTracking, writesSource]
  );

  // S020-8.20.6: a column another automation tracks is not taken; the notice
  // names it and leads there instead.
  const handleColumn = useCallback(
    (columnId: string) => {
      const trigger = config.triggerConfig;
      const watched =
        trigger?.type === 'row_changed' &&
        watchesStatusColumn(trigger.watchedColumnIds ?? [], columnId);
      setWatchedConflict(watched ? columnId : null);
      if (watched) return;
      const owner = usedColumns.get(columnId);
      setConflict(owner ?? null);
      if (owner) return;
      if (columnId) updateStatusTracking({ columnId });
      else updateWithoutTracking({ columnId: null });
    },
    [
      config.triggerConfig,
      updateStatusTracking,
      updateWithoutTracking,
      usedColumns,
    ]
  );

  const handleCreateStatusColumn = useCallback(async () => {
    if (!onCreateStatusColumn || creatingColumn) return;
    setCreatingColumn(true);
    try {
      await onCreateStatusColumn();
    } finally {
      setCreatingColumn(false);
    }
  }, [creatingColumn, onCreateStatusColumn]);

  const columnPicker =
    selectColumns.length === 0 ? (
      // Same place as the picker: the field's name, then its one action.
      // The setting's "Open" state already says the column is missing.
      <div className={styles.trackingField}>
        {t['com.streamlain.streamlain.tracking.status-column']()}
        {onCreateStatusColumn ? (
          <Button
            className={styles.trackingCreate}
            variant="secondary"
            prefix={<PlusIcon />}
            disabled={disabled || creatingColumn}
            onClick={() => void handleCreateStatusColumn()}
          >
            {t['com.streamlain.automation.sidebar.create-column']()}
          </Button>
        ) : (
          <span className={settingStyles.settingHint}>
            {t['com.streamlain.automation.sidebar.tracking-create-column']()}
          </span>
        )}
      </div>
    ) : (
      <label className={styles.trackingField}>
        {t['com.streamlain.streamlain.tracking.status-column']()}
        <TypedFieldSelect
          appearance="picker"
          searchable
          value={statusTracking.columnId ?? ''}
          groups={[
            {
              id: 'status-columns',
              label: '',
              options: selectColumns.map(column => ({
                id: column.id,
                label: column.name,
                type: column.type,
                description: usedColumns.has(column.id)
                  ? t['com.streamlain.streamlain.tracking.column-in-use']()
                  : undefined,
              })),
            },
          ]}
          onChange={handleColumn}
          placeholder={t['com.streamlain.streamlain.common.select']()}
          searchPlaceholder={t[
            'com.streamlain.dataview.toolbar.searchPlaceholder'
          ]()}
          emptyLabel={t['com.streamlain.cmdk.no-results']()}
          ariaLabel={t['com.streamlain.streamlain.tracking.status-column']()}
          triggerDataAttribute="data-status-column-picker"
          disabled={disabled || creatingColumn}
          actions={
            onCreateStatusColumn
              ? [
                  {
                    label:
                      t['com.streamlain.automation.sidebar.create-column'](),
                    icon: <PlusIcon />,
                    disabled: creatingColumn,
                    onSelect: () => void handleCreateStatusColumn(),
                  },
                ]
              : []
          }
        />
      </label>
    );

  // Automatic triggers need every value: rows with the error value are not
  // run again, the pending value shows the rows a run works on.
  const valuesRequired = needsStatusTracking(config);
  const valueSelect = (
    kind: 'pending' | 'success' | 'error',
    value: string,
    onChange: (value: string) => void
  ) => (
    <div className={styles.valueField}>
      <span className={styles.valueLabel}>
        <span className={styles.valueDot} data-kind={kind} />
        {t[`com.streamlain.streamlain.tracking.on-${kind}`]()}
      </span>
      <SettingSelect
        value={value}
        options={[
          ...(kind !== 'success' && !valuesRequired
            ? [
                {
                  value: '',
                  label: t['com.streamlain.streamlain.common.none'](),
                },
              ]
            : []),
          ...STATUS_STANDARD_VALUES[kind].map(option => ({
            value: option,
            label: option,
          })),
        ]}
        onChange={onChange}
        disabled={disabled}
        ariaLabel={t[`com.streamlain.streamlain.tracking.on-${kind}`]()}
        data-testid={`status-value-${kind}`}
      />
    </div>
  );

  return (
    <CollapsibleSection
      title={t['com.streamlain.automation.panel.tile.tracking']()}
      onInfoClick={onInfoClick}
    >
      <div className={styles.tracking} data-testid="status-tracking">
        <div className={styles.switchRow}>
          <span className={styles.switchText}>
            {t['com.streamlain.automation.sidebar.tracking-optional']()}
            {required && (
              <span className={styles.trackingLock}>
                <LockIcon />
                {t['com.streamlain.automation.sidebar.tracking-required']()}
              </span>
            )}
          </span>
          <Switch
            checked={statusTracking.enabled}
            disabled={disabled}
            aria-label={t['com.streamlain.automation.panel.tile.tracking']()}
            onChange={handleToggle}
          />
        </div>

        <AnimatedRegion expanded={statusTracking.enabled} keepMounted>
          <div className={styles.trackingBody}>
            {offersModeChoice && (
              <AnimatedPillSegment<TrackingMode>
                active={mode}
                ariaLabel={t[
                  'com.streamlain.streamlain.tracking.after-success'
                ]()}
                disabled={disabled}
                onChange={handleMode}
                options={[
                  {
                    key: 'status_column',
                    label: t['com.streamlain.streamlain.tracking.mode.mark'](),
                  },
                  {
                    key: 'delete_row',
                    label:
                      t['com.streamlain.streamlain.tracking.mode.delete'](),
                    disabled: writesSource && !deleting,
                  },
                ]}
              />
            )}
            {offersModeChoice && writesSource && !deleting && (
              <SectionHint>
                {t[
                  'com.streamlain.automation.sidebar.tracking-delete-unavailable'
                ]()}
              </SectionHint>
            )}
            <AnimatedRegion expanded animateContent>
              {deleting ? (
                writesSource ? (
                  <SectionNotice variant="warning" icon={<LockIcon />}>
                    <span role="alert">
                      {t[
                        'com.streamlain.automation.sidebar.tracking-delete-conflict'
                      ]()}
                    </span>
                  </SectionNotice>
                ) : (
                  <SectionNotice icon={<InformationFillDuotoneIcon />}>
                    {t['com.streamlain.streamlain.tracking.delete-row-info']()}
                  </SectionNotice>
                )
              ) : (
                <div className={styles.trackingSteps}>
                  {columnPicker}
                  {watchedConflict && (
                    <SectionNotice variant="warning" icon={<LockIcon />}>
                      <span role="alert" data-testid="status-column-watched">
                        {t[
                          'com.streamlain.automation.sidebar.trigger.status-column-watched'
                        ]({
                          name:
                            selectColumns.find(
                              column => column.id === watchedConflict
                            )?.name ?? '',
                        })}
                      </span>
                    </SectionNotice>
                  )}
                  {conflict && (
                    <SectionNotice
                      variant="warning"
                      icon={<LockIcon />}
                      action={
                        onNavigateToConflict && (
                          <button
                            type="button"
                            className={styles.conflictLink}
                            onClick={() => {
                              onNavigateToConflict(
                                conflict.conflictingAutomationId,
                                conflict.conflictingDocId
                              );
                              setConflict(null);
                            }}
                          >
                            {t[
                              'com.streamlain.streamlain.tracking.conflict-goto'
                            ]()}
                          </button>
                        )
                      }
                    >
                      <span role="alert">
                        {t[
                          'com.streamlain.streamlain.tracking.column-in-use-by'
                        ]({ name: conflict.conflictingAutomationName })}
                      </span>
                    </SectionNotice>
                  )}
                  {statusTracking.columnId && (
                    <div className={styles.valuesGrid}>
                      {valueSelect(
                        'pending',
                        statusTracking.pendingValue ?? '',
                        value =>
                          updateStatusTracking({
                            pendingValue: value || undefined,
                          })
                      )}
                      {valueSelect(
                        'success',
                        statusTracking.successValue,
                        value => updateStatusTracking({ successValue: value })
                      )}
                      {valueSelect(
                        'error',
                        statusTracking.errorValue ?? '',
                        value =>
                          updateStatusTracking({
                            errorValue: value || undefined,
                          })
                      )}
                    </div>
                  )}
                </div>
              )}
            </AnimatedRegion>
          </div>
        </AnimatedRegion>
      </div>
      <ConfirmModal
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t[
          'com.streamlain.streamlain.tracking.delete-row-confirm-title'
        ]()}
        description={t[
          'com.streamlain.streamlain.tracking.delete-row-confirm-description'
        ]()}
        confirmText={t[
          'com.streamlain.streamlain.tracking.delete-row-confirm'
        ]()}
        cancelText={t['com.streamlain.streamlain.common.cancel']()}
        autoFocusConfirm={false}
        confirmButtonOptions={{ variant: 'error' }}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => {
          updateStatusTracking({ mode: 'delete_row' });
          setConfirmDelete(false);
        }}
      />
    </CollapsibleSection>
  );
};
